import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import useBackdrop from '../hooks/useBackdrop';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import PageBaseLayout from '../layouts/PageBaseLayout';
import type { AppNotification } from '../models/types';
import { supabase } from '../supabase-client';
import { Box, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import moment from 'moment';
import SendNotificationDialog from '../components/SendNotificationDialog';

export default function Notifications({
  notificationId,
}: {
  notificationId?: string;
}) {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  // const handleUpdateWithdrawalRequest = (
  //   id: number,
  //   newStatus: 'pending' | 'completed' | 'rejected',
  //   newAmount: number,
  //   newRemarks: string | undefined,
  // ) => {
  //   setNotifications((oldWithdrawalRequests) => {
  //     const withdrawalRequest = oldWithdrawalRequests.find(
  //       (wr) => wr.id === id,
  //     );

  //     if (!withdrawalRequest)
  //       throw new Error('Could not find withdrawal request. Please refresh.');

  //     withdrawalRequest.status = newStatus;
  //     withdrawalRequest.amount = Math.abs(newAmount) * -1;
  //     withdrawalRequest.remarks = newRemarks ?? '';

  //     const newWithdrawalRequests = oldWithdrawalRequests.filter(
  //       (wr) => wr.id !== withdrawalRequest.id,
  //     );

  //     newWithdrawalRequests.push(withdrawalRequest);
  //     newWithdrawalRequests.sort(
  //       (a, b) =>
  //         new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  //     );
  //     return newWithdrawalRequests;
  //   });
  // };

  const columns: GridColDef<AppNotification>[] = [
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      type: 'actions',
      getActions: (params) => [
        <IconButton
          aria-label="mark-as-read"
          onClick={async () => {
            backdrop.handleBackdropOpen();
            dialog.handleDialogOpen(
              <SendNotificationDialog />,
              'Send Notification',
            );
            try {
              const { error } = await supabase
                .schema('app')
                .from('notifications')
                .delete()
                .eq('id', params.row.id);

              if (error) {
                console.error('Error deleting notification:', error);
                throw error;
              }

              setNotifications((oldNotifications) =>
                oldNotifications.filter((n) => n.id !== params.row.id),
              );
              snackbar.handleSnackbarOpen(
                'Notification marked as read',
                'success',
              );
            } catch (error) {
              const errorCast = error as Error;
              console.error(errorCast.stack);
              snackbar.handleSnackbarOpen(errorCast.message, 'error');
            }
            backdrop.handleBackdropClose();
          }}>
          <SendIcon color="primary" />
        </IconButton>,
      ],
      // renderCell: (param) => {
      //   const requestNotPending = param.row.status !== 'pending';

      //   return (
      //     <Box>
      //       <IconButton
      //         disabled={requestNotPending}
      //         aria-label="approve"
      //         onClick={() => {
      //           dialog.handleDialogOpen(
      //             <ProcessWithdrawalRequestDialog
      //               handleUpdateWithdrawalRequest={
      //                 handleUpdateWithdrawalRequest
      //               }
      //               withdrawalRequestId={param.row.id}
      //               amount={param.row.amount}
      //               newStatus="completed"
      //             />,
      //             `Approve Withdrawal Request - ${param.row.id}`,
      //           );
      //         }}>
      //         <DoneIcon color={requestNotPending ? 'disabled' : 'success'} />
      //       </IconButton>
      //       <IconButton
      //         disabled={requestNotPending}
      //         aria-label="reject"
      //         onClick={() => {
      //           dialog.handleDialogOpen(
      //             <ProcessWithdrawalRequestDialog
      //               handleUpdateWithdrawalRequest={
      //                 handleUpdateWithdrawalRequest
      //               }
      //               withdrawalRequestId={param.row.id}
      //               amount={param.row.amount}
      //               newStatus="rejected"
      //             />,
      //             `Reject Withdrawal Request - ${param.row.id}`,
      //           );
      //         }}>
      //         <ClearIcon color={requestNotPending ? 'disabled' : 'error'} />
      //       </IconButton>
      //     </Box>
      //   );
      // },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      type: 'dateTime',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 160,
    },
    {
      field: 'body',
      headerName: 'Body',
      width: 220,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
    },
    {
      field: 'payload',
      headerName: 'Payload',
      width: 110,
      type: 'string',
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 140,
      type: 'string',
    },
  ];

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .schema('app')
        .from('notifications')
        .select('*');

      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }

      setNotifications(data);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title="Notifications">
      <DataGrid columns={columns} rows={notifications} />
      <Outlet />
    </PageBaseLayout>
  );
}
